import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage, LocalizedPrice } from 'intl/LocalizedComponents';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _last from 'lodash/last';
import _map from 'lodash/map';
import _reject from 'lodash/reject';
import PropTypes from 'prop-types';
import getSymbolFromCurrency from 'currency-symbol-map';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import Card from 'components/core/Card';
import Select from 'components/core/Select';
import Table from 'components/core/table/Table';
import TableHead from 'components/core/table/TableHead';
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import TextField from 'components/core/TextField';
import Typography from 'components/core/Typography';
// Models
import mPriceGrid from 'models/cpm/priceGrid';
import mPriceRule from 'models/cpm/priceRule';
// Custom
import { cpmPriceGridDataShape, cpmPriceRuleDataShape, errorShape } from 'constants/PropTypesShapes';
import { sanitizeFloatRounded, sanitizeInt } from 'utils/Tools';
import {
  buildFormErrors, completeFormErrors, cpmPriceGridConstraints, cpmPriceGridTierConstraints,
} from 'utils/Forms';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
import {
  AddCircleIconButton, EditIconButton, DeleteIconButton, ValidateIconButton, CancelIconButton,
} from 'components/buttons/IconButtons';
import NumberInputField from 'components/inputFields/NumberInputField';
import Callout from 'components/misc/Callout';

class PriceGridAddEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleAddLineDurationChange = this.handleAddLineDurationChange.bind(this);
    this.handleAddLineUnitChange = this.handleAddLineUnitChange.bind(this);
    this.handleAddLineAmountChange = this.handleAddLineAmountChange.bind(this);
    this.handleAddTier = this.handleAddTier.bind(this);
    this.handleDeleteTier = this.handleDeleteTier.bind(this);
    this.handleCancelEditLine = this.handleCancelEditLine.bind(this);
    this.handleEditTierLine = this.handleEditTierLine.bind(this);
    this.handleTierLineAmountChange = this.handleTierLineAmountChange.bind(this);
    this.handleValidateTierLine = this.handleValidateTierLine.bind(this);
    this.handleEditAdditionalLine = this.handleEditAdditionalLine.bind(this);
    this.handleAdditionalLineAmountChange = this.handleAdditionalLineAmountChange.bind(this);
    this.handleValidateAdditionalLine = this.handleValidateAdditionalLine.bind(this);
    this.handleValidate = this.handleValidate.bind(this);

    // Create a ref for add line duration field (to be able to focus it)
    this.addLineDurationRef = React.createRef();

    this.state = {
      addLine: {
        duration: '',
        unit: mPriceGrid.getGridUnitMinute,
        amountInclTax: '',
        errors: null,
      },
      tierLine: {
        editionKey: null,
        amountInclTax: '',
      },
      additionalLine: {
        edition: false,
        amountInclTax: '',
      },
    };
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers
  handleAddLineDurationChange(value) {
    this.setState((state) => ({
      addLine: {
        ...state.addLine,
        duration: value,
      },
    }));
  }

  handleAddLineUnitChange(value) {
    const unit = mPriceGrid.tierUnit({ unit: value });
    this.setState((state) => ({
      addLine: {
        ...state.addLine,
        unit,
        duration: state.addLine.duration > unit.maxDuration ? unit.maxDuration : state.addLine.duration,
      },
    }));
  }

  handleAddLineAmountChange(value) {
    this.setState((state) => ({
      addLine: {
        ...state.addLine,
        amountInclTax: value,
      },
    }));
  }

  handleAddTier() {
    const { fields: { tiers, additional }, onFieldsChange } = this.props;
    const { addLine: { duration, unit, amountInclTax } } = this.state;

    const errors = buildFormErrors({ duration, amountInclTax }, cpmPriceGridTierConstraints);

    if (errors) {
      const { details: { duration: durationError, amountInclTax: amountInclTaxError } } = errors;
      this.setState((state) => ({
        addLine: {
          ...state.addLine,
          errors: {
            ...state.addLine.errors,
            duration: durationError ? <LocalizedMessage id={`formError.duration.${durationError[0]}`} /> : null,
            amountInclTax: amountInclTaxError ? <LocalizedMessage id={`formError.amountInclTax.${amountInclTaxError[0]}`} /> : null,
          },
        },
      }));
    } else if (_find(tiers, { duration, unit: unit.value })) {
      // This line cannot be added (because of duration/unit unicity)
      this.setState((state) => ({
        addLine: {
          ...state.addLine,
          errors: {
            ...state.addLine.errors,
            duration: <LocalizedMessage id="formError.priceGridDuration.duplicate" />,
          },
        },
      }));
    } else {
      // Add the new tier to the current tiers list
      onFieldsChange({
        tiers: [
          ...tiers,
          {
            amountInclTax: mPriceRule.serializeAmount(amountInclTax),
            duration,
            unit: unit.value,
          },
        ],
        // Also initialize additional if not already defined
        ...(!additional && {
          additional: { amountInclTax: 0 },
        }),
      });
      // Reinit addLine form
      this.setState((state) => ({
        addLine: {
          ...state.addLine,
          duration: '',
          amountInclTax: '',
          errors: null,
        },
      }), () => {
        // Focus on duration field
        this.addLineDurationRef.current.focus();
      });
    }
  }

  handleDeleteTier(tier) {
    const { fields: { tiers }, onFieldsChange } = this.props;
    onFieldsChange({
      tiers: _reject(tiers, (t) => (mPriceGrid.tierKey(t) === mPriceGrid.tierKey(tier))),
    });
  }

  handleCancelEditLine() {
    this.setState({
      tierLine: {
        editionKey: null,
        amountInclTax: '',
      },
      additionalLine: {
        edition: false,
        amountInclTax: '',
      },
    });
  }

  handleEditTierLine(tier) {
    this.setState({
      tierLine: {
        editionKey: mPriceGrid.tierKey(tier),
        amountInclTax: mPriceRule.displayAmount(tier.amountInclTax),
      },
    });
  }

  handleTierLineAmountChange(value) {
    this.setState((state) => ({
      tierLine: {
        ...state.tierLine,
        amountInclTax: value,
      },
    }));
  }

  handleValidateTierLine() {
    const { fields: { tiers }, onFieldsChange } = this.props;
    const { tierLine: { editionKey, amountInclTax } } = this.state;
    // Update tier
    onFieldsChange({
      tiers: _map(tiers, (tier) => (
        mPriceGrid.tierKey(tier) === editionKey ? {
          ...tier,
          amountInclTax: mPriceRule.serializeAmount(amountInclTax),
        } : tier
      )),
    });
    // Reinit additional line form
    this.setState({
      tierLine: {
        editionKey: null,
        amountInclTax: '',
      },
    });
  }

  handleEditAdditionalLine() {
    const { fields: { additional } } = this.props;
    this.setState({
      additionalLine: {
        edition: true,
        amountInclTax: mPriceRule.displayAmount(additional.amountInclTax),
      },
    });
  }

  handleAdditionalLineAmountChange(value) {
    this.setState((state) => ({
      additionalLine: {
        ...state.additionalLine,
        amountInclTax: value,
      },
    }));
  }

  handleValidateAdditionalLine() {
    const { onFieldsChange } = this.props;
    const { additionalLine: { amountInclTax } } = this.state;
    // Update additional amount
    onFieldsChange({
      additional: {
        amountInclTax: mPriceRule.serializeAmount(amountInclTax),
      },
    });
    // Reinit additional line form
    this.setState({
      additionalLine: {
        edition: false,
        amountInclTax: '',
      },
    });
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      fields: {
        name, tiers, additional,
      },
      priceGrid, onErrorsChange, onValidate,
    } = this.props;

    let errors = buildFormErrors({ name }, cpmPriceGridConstraints);
    // Check tiers
    if (_isEmpty(tiers)) {
      errors = completeFormErrors(errors, 'tiers', ['cantBeEmpty']);
    }
    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      onValidate(mPriceGrid.serialize({
        name, tiers, additional,
      }), priceGrid);
      return true;
    }
    return false;
  }

  render() {
    const {
      priceRule, fields: {
        name, tiers, additional,
      }, errors,
      onFieldsChange,
    } = this.props;
    const { addLine, tierLine, additionalLine } = this.state;

    // Price rule elements
    const { currency } = priceRule;

    // Current editions
    const additionalLineEdition = additionalLine.edition;
    const tierLineEditionKey = tierLine.editionKey;

    // Last unit
    const sortedTiers = mPriceGrid.sortTiers({ tiers });
    const lastUnit = !_isEmpty(tiers) && _last(sortedTiers).unit;

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          <div className="col-12">
            <div className="d-flex">
              <FormHelper elements={[]} />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12 col-md-6 col-lg-4">
                      <TextField
                        required
                        label={<LocalizedMessage id="field.name" />}
                        errorText={_get(errors, 'name')}
                        value={name}
                        onChange={(value) => onFieldsChange({ name: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
          <div className="col-12 pt-3">
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    content: <LocalizedMessage id="help.priceGrids.formTiers" />,
                  },
                  {
                    content: <LocalizedMessage id="help.priceGrids.formAdditional" />,
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card
                  title={(
                    <Typography type="subtitle2">
                      <LocalizedMessage id="field.gridDefinition" />
                    </Typography>
                  )}
                >
                  <div className="row">
                    {_has(errors, 'tiers') && (
                      <div className="col-12 pb-2">
                        <Callout severity="error" withInfoIcon>
                          {errors.tiers}
                        </Callout>
                      </div>
                    )}
                    <div className="col-12">
                      <Table
                        hasElements
                        head={(
                          <TableHead
                            columns={[
                              {
                                key: 'duration',
                                labelId: 'field.parkingDuration',
                                field: 'duration',
                                sortable: false,
                              },
                              {
                                key: 'price',
                                labelId: 'field.rate',
                                field: 'price',
                                sortable: false,
                                className: 'table-content-3 table-content-center',
                              },
                            ]}
                            adjustForActions
                            nbActions={2}
                          />
                        )}
                        withFooter={false}
                      >
                        <React.Fragment>
                          <TableRow hover={false} background="default">
                            <TableCell>
                              <NumberInputField
                                ref={this.addLineDurationRef}
                                value={addLine.duration}
                                fullWidth={false}
                                inputMaxWidth={150}
                                margin="none"
                                min={1}
                                max={addLine.unit.maxDuration}
                                sanitizer={sanitizeInt}
                                errorText={_get(addLine.errors, 'duration')}
                                disabled={additionalLineEdition || !!tierLineEditionKey}
                                adornments={{
                                  end: (
                                    <Select
                                      margin="none"
                                      fullWidth={false}
                                      disableUnderline
                                      value={addLine.unit.value}
                                      items={mPriceGrid.units}
                                      disabled={additionalLineEdition || !!tierLineEditionKey}
                                      onChange={this.handleAddLineUnitChange}
                                      onEnterPress={this.handleAddTier}
                                    />
                                  ),
                                }}
                                onChange={this.handleAddLineDurationChange}
                              />
                            </TableCell>
                            <TableCell className="table-content-3 table-content-center">
                              <NumberInputField
                                value={addLine.amountInclTax}
                                fullWidth={false}
                                inputMaxWidth={70}
                                margin="none"
                                min={0}
                                sanitizer={sanitizeFloatRounded(2)}
                                errorText={_get(addLine.errors, 'amountInclTax')}
                                adornments={{ start: getSymbolFromCurrency(currency) }}
                                disabled={additionalLineEdition || !!tierLineEditionKey}
                                onChange={this.handleAddLineAmountChange}
                                onEnterPress={this.handleAddTier}
                              />
                            </TableCell>
                            <TableCell className="table-actions-2" noPadding>
                              <AddCircleIconButton
                                primary
                                disabled={additionalLineEdition || !!tierLineEditionKey}
                                tooltipId="tooltip.addPriceGridTier"
                                onClick={this.handleAddTier}
                              />
                            </TableCell>
                          </TableRow>
                          {_map(sortedTiers, (tier) => {
                            const tierKey = mPriceGrid.tierKey(tier);
                            return (
                              <TableRow
                                key={tierKey}
                                background={tierLineEditionKey === tierKey ? 'primary' : null}
                                hover={!(tierLineEditionKey === tierKey)}
                              >
                                <TableCell>
                                  <LocalizedMessage id="info.duration" values={{ duration: tier.duration, unit: tier.unit }} />
                                </TableCell>
                                <TableCell className="table-content-3 table-content-center">
                                  {tierLineEditionKey === tierKey ? (
                                    <NumberInputField
                                      value={tierLine.amountInclTax}
                                      fullWidth={false}
                                      inputMaxWidth={70}
                                      margin="none"
                                      min={0}
                                      sanitizer={sanitizeFloatRounded(2)}
                                      adornments={{ start: getSymbolFromCurrency(currency) }}
                                      onChange={this.handleTierLineAmountChange}
                                      onEnterPress={this.handleValidateTierLine}
                                    />
                                  ) : (
                                    <Typography type="keyInfo4">
                                      {tier.amountInclTax === 0 ? (
                                        <LocalizedMessage id="info.free" />
                                      ) : (
                                        <LocalizedPrice
                                          currency={currency}
                                          value={mPriceRule.displayAmount(tier.amountInclTax)}
                                        />
                                      )}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell className="table-actions-2" noPadding>
                                  {tierLineEditionKey === tierKey ? (
                                    <React.Fragment>
                                      <ValidateIconButton primary onClick={this.handleValidateTierLine} />
                                      <CancelIconButton onClick={this.handleCancelEditLine} />
                                    </React.Fragment>
                                  ) : (
                                    <React.Fragment>
                                      <EditIconButton
                                        disabled={additionalLineEdition || (!!tierLineEditionKey && !(tierLineEditionKey === tierKey))}
                                        onClick={() => this.handleEditTierLine(tier)}
                                      />
                                      <DeleteIconButton
                                        disabled={additionalLineEdition || (!!tierLineEditionKey && !(tierLineEditionKey === tierKey))}
                                        onClick={() => this.handleDeleteTier(tier)}
                                      />
                                    </React.Fragment>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {!_isEmpty(tiers) && (
                            <TableRow background={additionalLineEdition ? 'primary' : null} hover={!additionalLineEdition}>
                              <TableCell noBorder>
                                <em><LocalizedMessage id="info.durationAdditional" values={{ unit: lastUnit }} /></em>
                              </TableCell>
                              <TableCell noBorder className="table-content-3 table-content-center">
                                {additionalLineEdition ? (
                                  <NumberInputField
                                    value={additionalLine.amountInclTax}
                                    fullWidth={false}
                                    inputMaxWidth={70}
                                    margin="none"
                                    min={0}
                                    sanitizer={sanitizeFloatRounded(2)}
                                    adornments={{ start: getSymbolFromCurrency(currency) }}
                                    onChange={this.handleAdditionalLineAmountChange}
                                    onEnterPress={this.handleValidateAdditionalLine}
                                  />
                                ) : (
                                  <Typography type="keyInfo4">
                                    {additional && (
                                      <LocalizedPrice
                                        currency={currency}
                                        value={mPriceRule.displayAmount(additional.amountInclTax)}
                                      />
                                    )}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell noBorder className="table-actions-2" noPadding>
                                {additionalLineEdition ? (
                                  <React.Fragment>
                                    <ValidateIconButton primary onClick={this.handleValidateAdditionalLine} />
                                    <CancelIconButton onClick={this.handleCancelEditLine} />
                                  </React.Fragment>
                                ) : (
                                  <EditIconButton disabled={!!tierLineEditionKey} onClick={this.handleEditAdditionalLine} />
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      </Table>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

PriceGridAddEditForm.propTypes = {
  operation: PropTypes.oneOf(['add', 'update']).isRequired, // eslint-disable-line react/no-unused-prop-types
  priceGrid: cpmPriceGridDataShape,
  priceRule: cpmPriceRuleDataShape.isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
};

PriceGridAddEditForm.defaultProps = {
  priceGrid: null,
  operationError: null,
  errors: null,
};

const mapFieldsToProps = {
  name: {
    resourceKey: 'name',
    defaultValue: '',
  },
  tiers: {
    resourceKey: 'tiers',
    defaultValue: [],
  },
  additional: {
    resourceKey: 'additional',
    defaultValue: null,
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: ownProps.priceGrid,
  operationError: ownProps.operationError,
});

const mapStateToProps = (state, ownProps) => ({
  operationError: _get(state.cpmCarparkPriceGrid, [ownProps.operation, 'error']),
});

module.exports = compose(
  connect(
    mapStateToProps,
  ),
  withFormManagement(
    mapFieldsToProps,
    mapOwnPropsToResources,
    false,
  ),
  withStateReinit(['addCarparkPriceGrid', 'updateCarparkPriceGrid']),
)(PriceGridAddEditForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/priceGrids/PriceGridAddEditForm.js